// ════════════════════════════════════════════════════════════════════════════
// The result-ref grammar — the ONE address family every search result mints.
//
// Ratified 2026-08-14 (planning/research-layer/serpers/pipeline/
// FOLLOWUP-CONTRACT.md). A search answer prints refs; `follow_up_search` takes
// them back. The model never composes an address — it pastes what a render
// handed it — so the grammar exists for OUR minting and OUR parsing, not as a
// syntax models are taught to build (doctrine D25/D26).
//
//   res-ref-<PILLAR><PROVIDER>-<searchSeq>[-<rowIndex>][#<action>]
//
//   res-ref-HOGO-12            search ref (hotels · google, search #12)
//   res-ref-JO-12              multi-lane aggregate search ref (jobs fan-out —
//                              the search has no single provider; its ROWS do)
//   res-ref-HOGO-12-3          row ref (row 3 of that search, 1-based,
//                              cumulative across pages)
//   res-ref-HOGO-12#more       action ref
//   res-ref-HIIA-12-1#search=religious tolerance
//
// This module lives in SHARED, not in the tool layer, for the same reason
// `parse-map-block.ts` does (packages/shared/src/business-logic/map/): the
// widget future (contract §8) runs a ref parse at ANSWER POST-PROCESSING, after
// the tool layer is done — the parser must be importable from both sides, and
// the map-widget pass is the standing precedent for where such a parse lives.
// The record/port types built over these enums sit beside `SearchBudgetPort`
// in ./types.ts, per the same workspace-visibility rule.
//
// String-level parsing is deliberately NOT routing authority (contract §2.2):
// resolution looks the record up by (searchSeq, rowIndex) and validates the
// parsed prefix AGAINST the record. This parser answers only "is this string
// the grammar", never "is this ref ours".
// ════════════════════════════════════════════════════════════════════════════

// §12 (RULED 2026-08-11): printed result refs must not be confusable with the
// platform's own id families — which is what the prefix buys. A body that is
// not the grammar does not parse (the head must be letters), so a mangled or
// invented `res-ref-…` string resolves to nothing rather than to somebody
// else's row.
export const RES_REF_PREFIX = 'res-ref-';
export const REF_SEGMENT_SEPARATOR = '-';
export const REF_FRAGMENT_SEPARATOR = '#';
export const REF_ACTION_VALUE_SEPARATOR = '=';
// Reserved by the grammar (`[&<key>=<value>]*`); the ruled action vocabulary is
// named actions only (fork §9.5 → named), so a fragment carrying pairs today is
// a parse failure, not a dispatch.
export const REF_ACTION_PARAM_SEPARATOR = '&';

// One code per pillar (contract §2.4, proposed registry adopted verbatim).
// Two uppercase letters, unique across the table — the enum IS the registry,
// and the no-loose-string rule applies at every use site.
export enum PillarCode {
  Hotels = 'HO',
  Flights = 'FL',
  Jobs = 'JO',
  Products = 'PR',
  Places = 'PL',
  RealEstate = 'RE',
  Academic = 'AC',
  Patents = 'PT',
  Social = 'SO',
  Youtube = 'YT',
  Books = 'BO',
  ClassicTexts = 'CT',
  HistoricalDocuments = 'HI',
  Scriptures = 'SR',
  Github = 'GH',
  News = 'NW',
  WebSearch = 'WB',
  Answer = 'AN',
  MediaVisual = 'MV',
  Concerts = 'CN',
  Finance = 'FI',
  Trends = 'TR',
  Weather = 'WX',
  Knowledge = 'KN',
  Apps = 'AP',
  Music = 'MU',
  // The 2026-08-28 wave: exercise & training (ymove) and food & nutrition
  // (FatSecret).
  Workouts = 'WK',
  Nutrition = 'NU',
}

// PROVIDER codes are deliberately NOT one shared enum. The registry is scoped
// PER PILLAR (contract §2.1): two letters stay unique within a pillar, and the
// same pair may recur across pillars (`GO` is google-hotels under HO and
// google-finance under FI). Each pillar wave authors its own
// `enum <Pillar>ProviderCode` beside its executor and passes the members at
// mint time — the same shape `SerpLane.actionId: string` already takes for the
// per-provider action enums. Registration validates form and within-pillar
// uniqueness (see toolers/follow-up/follow-up-executors.ts).
const PROVIDER_CODE_PATTERN = /^[A-Z]{2}$/;

export function isProviderCode(value: string): boolean {
  return PROVIDER_CODE_PATTERN.test(value);
}

// The membership question the registries answer: "is this two-letter code a
// provider THIS pillar registered?" The store consults it at mint and restore,
// the tool at dispatch — one authority, three enforcement points, so a pillar
// can never mint (or resurrect, or dispatch) a code its own registration never
// declared. Both registration barrels and every suite build their
// implementation through `providerMembershipOf`, which is also where the
// registry RULES live: two uppercase letters, unique within a pillar, one
// entry per pillar — validated here once so no barrel can hold an entry the
// grammar refuses.
export interface FollowUpProviderMembership {
  has(input: { pillar: PillarCode; code: string }): boolean;
}

export function providerMembershipOf(
  entries: readonly { pillar: PillarCode; codes: Record<string, string> }[],
): FollowUpProviderMembership {
  const byPillar = new Map<PillarCode, ReadonlySet<string>>();
  entries.forEach((entry) => {
    if (byPillar.has(entry.pillar)) {
      throw new Error(`duplicate provider registration for pillar ${entry.pillar}`);
    }
    const codes = Object.values(entry.codes);
    codes.forEach((code) => {
      if (!isProviderCode(code)) {
        throw new Error(
          `pillar ${entry.pillar} provider code "${code}" is not two uppercase letters`,
        );
      }
    });
    if (new Set(codes).size !== codes.length) {
      throw new Error(`pillar ${entry.pillar} registers a duplicate provider code`);
    }
    byPillar.set(entry.pillar, new Set(codes));
  });
  return {
    has: ({ pillar, code }) => byPillar.get(pillar)?.has(code) ?? false,
  };
}

// The action vocabulary (contract §5; forks §9.4 ruled BOTH `#more` and
// `#page=N`, §9.5 ruled NAMED actions over fragment params). Closed set: an
// unknown token after `#` is a parse failure, and whether a KNOWN action is
// available on a given ref is the record's `supportedActions` — never this
// enum's — to answer.
export enum FollowUpAction {
  // Next page of a search (chain lanes walk one step; ordinal lanes serve
  // served + 1). Also legal on row refs whose kind has a paged sub-list.
  More = 'more',
  // Ordinal jump — `#page=N`. Refused by lanes whose ordinal is not verified
  // faithful; the executor owns that refusal.
  Page = 'page',
  // The drill: full record / posting body / stored-raw-URL fetch on link rows.
  Details = 'details',
  // Passage search inside a document-kind row — `#search=<phrase>`, the phrase
  // taken VERBATIM to the end of the string, spaces included.
  Search = 'search',
  // Named enum drills (fork §9.5 ruled: the selecting token IS the action).
  Transcript = 'transcript',
  Comments = 'comments',
  Stats = 'stats',
  Citations = 'citations',
  References = 'references',
}

// A parsed fragment. Valued actions carry their payload; every other action is
// its name alone — `#more=3` is not a bigger `#more`, it is a parse failure.
export type FollowUpRefAction =
  | { readonly kind: FollowUpAction.Page; readonly page: number }
  | { readonly kind: FollowUpAction.Search; readonly phrase: string }
  | {
      readonly kind: Exclude<
        FollowUpAction,
        FollowUpAction.Page | FollowUpAction.Search
      >;
    };

// How pagination state advances on a search record (contract §3.2). Chain
// lanes hold a vendor cursor; ordinal lanes hold only the served count; None
// is a lane with one page (the refusal to page it is honest, not silent).
export enum PageMode {
  Ordinal = 'ordinal',
  Chain = 'chain',
  None = 'none',
}

// What a row IS, pillar-scoped (contract §3.2: "property, posting, paper,
// document…" — plus Link, the §5.1 kind whose `#details` fetches the stored
// raw URL). Seeded with exactly the kinds the ratified contract names; each
// pillar wave ADDS its members as it mints (D28: no kind ships before the wave
// that observes it).
// Pre-seeded for the full Phase 2 wave set (2026-08-14) so ten concurrent wave
// agents never contend on this shared file: every kind a contract §5.3 pillar
// mints is already here. Namespacing is NOT needed — the (pillar, kind) pair is
// what dispatch reads, so `Property` under HO and under RE are distinct by
// construction (the cross-pillar `'property'` collision of the old per-tool
// payloads cannot recur).
export enum RowKind {
  Property = 'property',
  Listing = 'listing',
  Posting = 'posting',
  Paper = 'paper',
  Patent = 'patent',
  Document = 'document',
  Section = 'section',
  Verse = 'verse',
  Translation = 'translation',
  Work = 'work',
  Author = 'author',
  Edition = 'edition',
  Subject = 'subject',
  Link = 'link',
  Product = 'product',
  App = 'app',
  Place = 'place',
  Post = 'post',
  Profile = 'profile',
  Video = 'video',
  Channel = 'channel',
  // Rows that carry no action of their own and print no ref line today: they
  // mint because the ruling is "every result mints" — the record is what a
  // widget will read (contract §8). Two waves correctly refused to borrow a
  // wrong kind for these rather than edit this file mid-flight.
  Review = 'review',
  Comment = 'comment',
  Repo = 'repo',
  File = 'file',
  User = 'user',
  Itinerary = 'itinerary',
  Quote = 'quote',
  Event = 'event',
  // The music pillar's three answer shapes (2026-08-18, the wave that closed
  // its U3 print-pass gap): a recording, an album, an artist card.
  Recording = 'recording',
  Album = 'album',
  Artist = 'artist',
  // The workouts pillar's two answer shapes (2026-08-28): one exercise from
  // the catalog, and one generated workout plan.
  Exercise = 'exercise',
  Workout = 'workout',
  // The nutrition pillar's two answer shapes (2026-08-28): a food with its
  // nutrition panel behind it, and a recipe.
  Food = 'food',
  Recipe = 'recipe',
}

// ─── Parsing ────────────────────────────────────────────────────────────────

// Why the parse NEVER throws: a malformed ref is a MODEL input, and the tool
// turns the failure into a typed tool-recoverable refusal. A throw here would
// ride the internal-error path, reset the attempt, and tell the model the
// failure was "not anything you did" — false, and exactly what the refusal
// pipeline exists to avoid.
export enum RefParseFailureKind {
  // The string does not open with the ref prefix at all.
  NotARef = 'not_a_ref',
  // Prefix present, but the head/searchSeq/rowIndex shape is not the grammar.
  MalformedBody = 'malformed_body',
  // A `#` fragment that is not a readable action: an unknown token, a value on
  // a valueless action, `#page` without its number, `#search=` with nothing
  // after it, or `&key=value` pairs (reserved grammar, no ruled consumer).
  MalformedAction = 'malformed_action',
}

export interface RefParseFailure {
  readonly ok: false;
  readonly kind: RefParseFailureKind;
  readonly input: string;
}

export interface ParsedFollowUpRef {
  readonly ok: true;
  readonly pillar: PillarCode;
  // null = the 2-letter aggregate head (`res-ref-JO-12`): a multi-lane search
  // ref carries the pillar only. A 4-letter head names the lane.
  readonly provider: string | null;
  readonly searchSeq: number;
  readonly rowIndex: number | null;
  readonly action: FollowUpRefAction | null;
}

export type RefParseResult = ParsedFollowUpRef | RefParseFailure;

// Strict positive integer — `012` does not parse. A minted ref never carries a
// leading zero, and this is a paste-back grammar: a digit string a mint could
// not have produced is not one of ours, whatever it "would round-trip to".
const POSITIVE_INT_PATTERN = /^[1-9]\d*$/;
// 2 letters = aggregate search head; 4 = pillar + lane. Uppercase only — the
// mint never lowercases, so a lowercased head is not a paste-back.
const HEAD_PATTERN = /^[A-Z]{2}(?:[A-Z]{2})?$/;
const PILLAR_CODE_LENGTH = 2;

// THE one integer reading for searchSeq, rowIndex and `#page=N`. Shape alone
// is not enough: `parseInt` over `9007199254740993` answers `…992` — a DIFFERENT
// address that a mint may genuinely have issued — and a few-hundred-digit run
// answers `Infinity`. A digit string whose value the number type cannot hold
// exactly is therefore not "a big ref", it is a collision generator, and no
// mint can produce one (the store's counters are safe integers by
// construction). Null = not a positive safe integer, in shape or in value.
export function parsePositiveSafeInt(segment: string): number | null {
  if (!POSITIVE_INT_PATTERN.test(segment)) return null;
  const value = Number.parseInt(segment, 10);
  return Number.isSafeInteger(value) && value > 0 ? value : null;
}

// The formatter side of the same rule: composing a ref from an invalid
// internal value would PRINT an address the parser refuses (or one that
// collides), and every caller of `formatRef` is our own code — so this throws,
// it never refuses politely.
function assertRefInt(input: { label: string; value: number }): void {
  if (Number.isSafeInteger(input.value) && input.value > 0) return;
  throw new Error(
    `${input.label} must be a positive safe integer, got ${input.value}`,
  );
}

const PILLAR_CODE_SET: ReadonlySet<string> = new Set(Object.values(PillarCode));
const ACTION_NAME_SET: ReadonlySet<string> = new Set(Object.values(FollowUpAction));

// The registry-membership twin of `isProviderCode`, for the one place a pillar
// arrives as DATA rather than as the enum type: a restored snapshot. A seed
// whose pillar is not in the registry composes canonical-LOOKING refs
// (`res-ref-XX-1` is canonical for pillar "XX"), so canonicality alone cannot
// catch it — membership must be asked by name.
export function isPillarCode(value: string): boolean {
  return PILLAR_CODE_SET.has(value);
}
const SEARCH_ACTION_OPENER =
  `${FollowUpAction.Search}${REF_ACTION_VALUE_SEPARATOR}`;

export function parseRef(input: string): RefParseResult {
  if (!input.startsWith(RES_REF_PREFIX)) {
    return { ok: false, kind: RefParseFailureKind.NotARef, input };
  }
  // Split at the FIRST `#` only: everything after it belongs to the action,
  // and `#search=` in particular owns the raw remainder — `#`, `&`, `=` and
  // spaces included — so no later separator may be consulted first.
  const fragmentAt = input.indexOf(REF_FRAGMENT_SEPARATOR);
  const body = fragmentAt === -1
    ? input.slice(RES_REF_PREFIX.length)
    : input.slice(RES_REF_PREFIX.length, fragmentAt);
  const fragment = fragmentAt === -1 ? null : input.slice(fragmentAt + 1);

  const segments = body.split(REF_SEGMENT_SEPARATOR);
  if (segments.length < 2 || segments.length > 3) {
    return { ok: false, kind: RefParseFailureKind.MalformedBody, input };
  }
  const [head, seqSegment, rowSegment] = segments;
  if (!HEAD_PATTERN.test(head)) {
    return { ok: false, kind: RefParseFailureKind.MalformedBody, input };
  }
  const searchSeq = parsePositiveSafeInt(seqSegment);
  if (searchSeq === null) {
    return { ok: false, kind: RefParseFailureKind.MalformedBody, input };
  }
  const rowIndex = rowSegment === undefined
    ? null
    : parsePositiveSafeInt(rowSegment);
  if (rowSegment !== undefined && rowIndex === null) {
    return { ok: false, kind: RefParseFailureKind.MalformedBody, input };
  }
  const pillarCandidate = head.slice(0, PILLAR_CODE_LENGTH);
  if (!PILLAR_CODE_SET.has(pillarCandidate)) {
    return { ok: false, kind: RefParseFailureKind.MalformedBody, input };
  }

  const action = fragment === null ? null : parseAction({ fragment });
  if (action !== null && 'failed' in action) {
    return { ok: false, kind: RefParseFailureKind.MalformedAction, input };
  }
  return {
    ok: true,
    pillar: pillarCandidate as PillarCode,
    provider: head.length > PILLAR_CODE_LENGTH
      ? head.slice(PILLAR_CODE_LENGTH)
      : null,
    searchSeq,
    rowIndex,
    action,
  };
}

// Internal sentinel — never escapes `parseRef`, which folds it into the typed
// failure. A discriminated object rather than null so "no fragment" and "an
// unreadable fragment" cannot be confused.
interface ActionParseFailed {
  readonly failed: true;
}

function parseAction(input: {
  fragment: string;
}): FollowUpRefAction | ActionParseFailed {
  const { fragment } = input;
  if (fragment.length === 0) return { failed: true };
  // `#search=` consumes EVERYTHING after `search=`, verbatim — it is the last
  // thing in the string by definition, so it is read before any `&` split
  // could eat a phrase that legitimately contains one.
  if (fragment.startsWith(SEARCH_ACTION_OPENER)) {
    const phrase = fragment.slice(SEARCH_ACTION_OPENER.length);
    if (phrase.length === 0) return { failed: true };
    return { kind: FollowUpAction.Search, phrase };
  }
  // Reserved `&key=value` grammar with no ruled consumer: named actions won
  // fork §9.5, so pairs are a parse failure rather than a silently-dropped
  // suffix the model would believe was honoured.
  if (fragment.includes(REF_ACTION_PARAM_SEPARATOR)) return { failed: true };

  const valueAt = fragment.indexOf(REF_ACTION_VALUE_SEPARATOR);
  const name = valueAt === -1 ? fragment : fragment.slice(0, valueAt);
  const value = valueAt === -1 ? null : fragment.slice(valueAt + 1);
  if (!ACTION_NAME_SET.has(name)) return { failed: true };
  const action = name as FollowUpAction;
  if (action === FollowUpAction.Page) {
    // The same safe-integer reading as the address segments: a page number
    // the number type would silently round is a jump to a page nobody asked
    // for, not a big ask.
    const page = value === null ? null : parsePositiveSafeInt(value);
    if (page === null) return { failed: true };
    return { kind: FollowUpAction.Page, page };
  }
  // `search` without `=` falls through to here (its opener check above needs
  // the separator), and every named action refuses a value: `#more=3` asks for
  // a grammar `#more` does not have.
  if (action === FollowUpAction.Search || value !== null) {
    return { failed: true };
  }
  return { kind: action } as FollowUpRefAction;
}

// ─── Formatting ─────────────────────────────────────────────────────────────

export interface FormatRefInput {
  readonly pillar: PillarCode;
  // null formats the aggregate head (search refs of multi-lane searches).
  readonly provider: string | null;
  readonly searchSeq: number;
  readonly rowIndex?: number;
  readonly action?: FollowUpRefAction;
}

// The single composer every ref string comes from — mints, refusal copy and
// tests all format here, so a printed ref and a parsed one can never drift
// (D12: wire tokens derive, never hand-typed). It THROWS on invalid internal
// input rather than emitting a string the parser would refuse: every caller is
// our own code, and a printed-but-unparseable ref is a lie handed to a model.
export function formatRef(input: FormatRefInput): string {
  assertRefInt({ label: 'searchSeq', value: input.searchSeq });
  if (input.rowIndex !== undefined) {
    assertRefInt({ label: 'rowIndex', value: input.rowIndex });
  }
  if (input.provider !== null && !isProviderCode(input.provider)) {
    throw new Error(
      `provider code "${input.provider}" is not two uppercase letters`,
    );
  }
  const head = `${input.pillar}${input.provider ?? ''}`;
  const rowPart = input.rowIndex === undefined
    ? ''
    : `${REF_SEGMENT_SEPARATOR}${input.rowIndex}`;
  const actionPart = input.action === undefined
    ? ''
    : `${REF_FRAGMENT_SEPARATOR}${formatRefAction(input.action)}`;
  return `${RES_REF_PREFIX}${head}${REF_SEGMENT_SEPARATOR}${input.searchSeq}${rowPart}${actionPart}`;
}

// The fragment alone, without its `#` — composition owns the separator. The
// page value gets the same guard as the address integers, and a search phrase
// must actually SAY something: the parser refuses `#search=` with an empty
// remainder, so formatting one would print the exact printed-but-unparseable
// ref this composer exists to make impossible. A phrase with content formats
// verbatim, whitespace included.
export function formatRefAction(action: FollowUpRefAction): string {
  if (action.kind === FollowUpAction.Page) {
    assertRefInt({ label: 'page', value: action.page });
    return `${FollowUpAction.Page}${REF_ACTION_VALUE_SEPARATOR}${action.page}`;
  }
  if (action.kind === FollowUpAction.Search) {
    if (action.phrase.trim().length === 0) {
      throw new Error('a search action needs a non-empty phrase');
    }
    return `${FollowUpAction.Search}${REF_ACTION_VALUE_SEPARATOR}${action.phrase}`;
  }
  return action.kind;
}

// The address with no fragment — the identity a record stores as `ref` and the
// string a prefix-mismatch refusal names (contract §4.2: "the record's
// canonical ref is named").
export function canonicalRef(input: {
  readonly pillar: PillarCode;
  readonly provider: string | null;
  readonly searchSeq: number;
  readonly rowIndex?: number;
}): string {
  return formatRef({
    pillar: input.pillar,
    provider: input.provider,
    searchSeq: input.searchSeq,
    rowIndex: input.rowIndex,
  });
}

// The grammar's SHAPE, for the malformed-ref refusals (contract §4.1 step 1:
// "typed refusal quoting the grammar"). Rendered slot for slot from the
// ratified grammar line's BODY — `res-ref-<PILLAR><PROVIDER>-<searchSeq>
// [-<rowIndex>]` — in the `sidShape` tradition: placeholders name each slot's
// role, and the prefix and separators interpolate the constants so the quoted
// shape cannot drift from the parser. The action slot no longer rides the
// shape (ruled 2026-08-23: refusal output speaks the typeable `?include=`
// vocabulary, so the sentence that quotes this shape teaches the action
// spelling itself, and a `#`-fragment must not print in any refusal).
export function refGrammarShape(): string {
  return `${RES_REF_PREFIX}<PILLAR><PROVIDER>${REF_SEGMENT_SEPARATOR}<searchSeq>[${REF_SEGMENT_SEPARATOR}<rowIndex>]`;
}

// ─── Leak detection ─────────────────────────────────────────────────────────

// The persistence leak guard's detector (build ruling, fork §9.3 rider: "fail
// any field that attempts to write the res-ref itself into it from the
// model"). Two-stage: find every case-insensitive occurrence of the prefix
// with a real LEFT boundary, then classify the tail as an ADDRESS shape or as
// hyphenated prose.
//
// Boundary — Unicode-aware over CODE POINTS, marks included: `\b` treats
// every non-ASCII letter as a boundary, so `éres-ref-1` and
// `中文res-ref-HOGO-1` would false-positive under it; a UTF-16 unit check
// fails two subtler shapes the same way — a DECOMPOSED accent (`e` + U+0301)
// puts a combining MARK before the prefix, and an astral letter (𝐀) puts a
// lone low surrogate there. The rule: reconstruct the full preceding code
// point (surrogate pair aware) and block on `\p{L}`, `\p{N}` or `\p{M}` — a
// mark rides the letter it modifies, so it blocks like one. `(res-ref-…)`,
// quotes, whitespace and Markdown link parentheses still match; embedded runs
// do not.
//
// Tail — an address is flagged, prose is not:
//   digits                       → a plain-number body: not the grammar, so
//                                  equally dangling, and address-shaped
//   2–8 alphanumerics `-` digits → the grammar and its mangles: case variants,
//                                  digit-substituted heads (`H0GO-1`),
//                                  over-long heads (`HOGOX-1`) — anything
//                                  head-plus-dash-digits is an ADDRESS shape,
//                                  because prose never numbers itself
//   2–8 UPPERCASE letters        → a truncated/mangled address head
//                                  (`res-ref-HOGO`, `res-ref-HOGOX-`)
//   nothing alphanumeric         → the bare mangled prefix (`res-ref-`)
//   a lowercase word             → hyphenated PROSE (`res-ref-style prose`) —
//                                  not flagged; prose ABOUT the grammar is
//                                  not an address in saved content
//
// The reported mention is the ADDRESS SPAN ONLY — trailing punctuation and a
// fragment's free-text tail are not part of the name, so `(res-ref-HOGO-12):`
// reports `res-ref-HOGO-12`, not `res-ref-HOGO-12):`.
const RES_REF_PREFIX_SCAN = new RegExp(RES_REF_PREFIX, 'gi');
const BOUNDARY_BLOCKING_CODE_POINT = /[\p{L}\p{N}\p{M}]/u;
const LOW_SURROGATE_MIN = 0xdc00;
const LOW_SURROGATE_MAX = 0xdfff;
// Address tails, tried in order; the first match IS the reported span (after
// the prefix). Broadest first, so the fullest address span is what a refusal
// names.
const ADDRESS_TAIL_PATTERNS: readonly RegExp[] = [
  /^[A-Za-z0-9]{2,8}-\d+(?:-\d+)?/, // grammar + mangled heads, dash-digits anchored
  /^\d+/, // plain-number body: address-shaped, resolves nowhere
  /^[A-Z]{2,8}(?![A-Za-z0-9])/, // truncated/mangled uppercase head
];

// True when the character IMMEDIATELY before `start` is a letter, digit or
// combining mark — read as a whole code point, so an astral letter's low
// surrogate and a decomposed accent's mark both count as the letters they are.
function blockedOnTheLeft(input: { text: string; start: number }): boolean {
  const { text, start } = input;
  if (start === 0) return false;
  let index = start - 1;
  const unit = text.charCodeAt(index);
  if (unit >= LOW_SURROGATE_MIN && unit <= LOW_SURROGATE_MAX && index > 0) {
    index -= 1;
  }
  const codePoint = text.codePointAt(index);
  return codePoint !== undefined
    && BOUNDARY_BLOCKING_CODE_POINT.test(String.fromCodePoint(codePoint));
}

export function findResRefMentions(text: string): string[] {
  const mentions = [...text.matchAll(RES_REF_PREFIX_SCAN)].flatMap((match) => {
    // `matchAll` always sets the index; the fallback only satisfies lib
    // typings where it is declared optional.
    const start = match.index ?? 0;
    if (blockedOnTheLeft({ text, start })) return [];
    const prefix = match[0];
    const tail = text.slice(start + prefix.length);
    const addressTail = ADDRESS_TAIL_PATTERNS
      .map((pattern) => pattern.exec(tail)?.[0])
      .find((span): span is string => span !== undefined);
    if (addressTail !== undefined) return [`${prefix}${addressTail}`];
    // No address shape and no word either: the bare mangled prefix is flagged
    // (anything WEARING the prefix as an address fails); a following letter
    // run without an address shape is hyphenated prose and is not.
    const nextChar = tail[0];
    return nextChar !== undefined && BOUNDARY_BLOCKING_CODE_POINT.test(nextChar)
      ? []
      : [prefix];
  });
  return [...new Set(mentions)];
}
